import { courseKey, courseQueries } from "@circle-react/lib/queries/courses";
import type {
  CourseLessonResponse,
  CourseSectionsResponse,
} from "@circle-react/lib/queries/courses.types";
import {
  useQuery,
  type keepPreviousData,
  type QueryFunction,
  type QueryKey,
  type UseQueryOptions,
} from "react-query";

type CourseParams = Record<string, unknown>;
type CourseId = number | string;

const requireQueryFn = <Response, Key extends QueryKey>(
  queryFn: QueryFunction<Response, Key> | undefined,
) => {
  if (!queryFn) {
    throw new Error("Course query is missing queryFn");
  }

  return queryFn;
};

interface QueryOverrides<Response> {
  enabled?: boolean;
  initialData?: Response;
  placeholderData?: typeof keepPreviousData;
  refetchOnWindowFocus?: boolean;
  retry?: number | false;
}

interface UseCourseSectionsOptions extends QueryOverrides<CourseSectionsResponse> {
  params?: CourseParams;
}

interface UseLessonOptions {
  courseId?: CourseId;
  lessonId?: CourseId;
  options?: QueryOverrides<CourseLessonResponse>;
  sectionId?: CourseId;
}

export const getCourseSectionsKey = (courseId?: CourseId) =>
  courseKey.key(Number(courseId) || 0, "sections");

export const useCourseSections = (
  courseId?: CourseId,
  options: UseCourseSectionsOptions = {},
) => {
  const { params = {}, ...queryOptions } = options;
  const query = courseQueries.sections(courseId, params);
  const courseSectionsOptions: UseQueryOptions<
    CourseSectionsResponse,
    unknown,
    CourseSectionsResponse,
    typeof query.queryKey
  > = {
    queryKey: query.queryKey,
    queryFn: requireQueryFn(query.queryFn),
    enabled: !!courseId,
    ...queryOptions,
  };

  return useQuery(courseSectionsOptions);
};

export const getLessonKey = (courseId?: CourseId, lessonId?: CourseId) =>
  courseKey.key(Number(courseId) || 0, "lessons", Number(lessonId) || 0);

export const useLesson = ({
  courseId,
  lessonId,
  sectionId,
  options = {},
}: UseLessonOptions) => {
  const query = courseQueries.lesson(courseId, lessonId, sectionId);
  const lessonOptions: UseQueryOptions<
    CourseLessonResponse,
    unknown,
    CourseLessonResponse,
    typeof query.queryKey
  > = {
    queryKey: query.queryKey,
    queryFn: requireQueryFn(query.queryFn),
    enabled: !!lessonId,
    ...options,
  };

  return useQuery(lessonOptions);
};
